/* eslint-disable @typescript-eslint/no-unused-vars */
import { computed, observable } from 'mobx';
import { TrackSchema } from '../../../types/schema';
import findFilters from '../../../lib/findFilters';
import { SearchSuggestionType, SuggestionType } from '../../../components/project-happy/atoms/SearchSuggestionItem';
import {
  FindTracksQueryVariables,
  FindSortMode,
  FindSortOrder,
  FindFiltersInput,
  GetAllMoodsAndGenresQuery,
  GetSearchSuggestionsQuery,
  GetValidatedArtistQuery,
  GetValidatedTrackQuery,
  GetPlaylistQuery,
} from '../../../graphql/generated-types/graphql';
import { formatGqlPlaylist } from '../../../components/project-happy/utilities/search';
import { isEmpty } from '../../../components/project-happy/utilities/objects';

export type Moods = GetAllMoodsAndGenresQuery['moods']['matches']['results'];
export type Genres = GetAllMoodsAndGenresQuery['genres']['matches']['results'];

export interface Instrument {
  label: string;
  value: boolean | undefined;
}
export interface Catalogue {
  label: string;
  value: string;
}
export type UIRange = [number, number];

export interface UILocalTempo {
  range: UIRange;
  label: string;
}

export enum TracksViewType {
  DEFAULT = 'default',
  TRACK = 'tracks',
  ARTIST = 'artist',
  HIDDEN = 'hidden',
}

export interface TrackEntity extends TrackSchema {
  name: string;
  type: SuggestionType | null;
}
export interface SelectedSuggestion {
  uuid?: string;
  identity?: string;
  name?: string;
  type?: SuggestionType | null;
  images?: any;
  slug?: string;
  tracks?: TrackSchema[];
  title?: string;
  artistSlug?: string;
  is_available_in_territory?: boolean;
}

export type ValidatedResultsType = GetValidatedArtistQuery | GetValidatedTrackQuery | GetPlaylistQuery;

const entityMap = { artists: 'artist', playlists: 'playlist', moods: 'mood', genres: 'genre', tracks: 'track' };

export const getDefaultState = (): FindTracksQueryVariables => ({
  query: '',
  pagination: {
    size: 25,
    from: 0,
    page: 0,
  },
  sortMode: FindSortMode.PopularSpotify,
  sortOrder: FindSortOrder.Desc,
  filters: findFilters({
    matchArtistFallback: true,
  }),
});

// Temporary implementation of 'instrumental' search suggestion [WEB-8279]
const instrumentalEntity = {
  type: 'instrumental',
  name: 'Instrumental',
  slug: null,
};

export class SearchMvpPageModel {
  @observable
  loading = false;

  @observable
  loadingMoreResults = false;

  @observable
  tracksApiError = false;

  @observable
  suggestionsApiError = false;

  @observable
  filterCountBounce = false;

  @observable
  noSearchResult = false;

  @observable
  pagination = {
    size: 25,
    from: 0,
    page: 0,
    total: 0,
    totalPages: 0,
  };

  @observable
  showSuggestionDropdown = false;

  @observable
  loadingSuggestions = false;

  @observable
  openFilter = false;

  @observable
  searchState: FindTracksQueryVariables = getDefaultState();

  @observable
  filters: FindFiltersInput & { catologueType?: null } = findFilters({
    matchArtistFallback: false,
    catalogueType: null,
  });

  @observable
  selectedSuggestion: SelectedSuggestion = null;

  @observable
  playlistDetails: any = { name: '', type: null, uuid: '', track_images: [] };

  @observable
  userRegion = '';

  @observable
  results: any[] = [];

  @observable
  suggestions: GetSearchSuggestionsQuery = {};

  @observable
  validatedSearchParams: { identity: string; type: string } = null;

  @observable
  validatedSearchResults: ValidatedResultsType = null;

  @computed
  get validatedSearchTrackFilters(): FindTracksQueryVariables {
    if (!this.validatedSearchResults || !this.selectedSuggestion) return {};

    if ('artist' in this.validatedSearchResults && !this.validatedSearchResults.artist.is_non_active_artist) {
      return {
        query: '',
        filters: {
          artist: this.validatedSearchResults.artist.identity,
        },
      };
    }

    return {};
  }

  // Temporary implementation of 'instrumental' search suggestion [WEB-8279]
  @observable
  showInstrumentalSuggestion = false;

  @observable
  searchHistory: any[] = [];

  @observable
  genres: Genres = [];

  @observable
  moods: Moods = [];

  @computed get durationRange() {
    return [0, 1040000];
  }

  @computed get bpmRange() {
    return [60, 300];
  }
  @observable tempos: Array<UILocalTempo> = [
    {
      range: [0, 80],
      label: 'Slower',
    },
    {
      range: [80, 110],
      label: 'Slow',
    },
    {
      range: [110, 130],
      label: 'Medium',
    },
    {
      range: [130, 150],
      label: 'Fast',
    },
    {
      range: [150, 300],
      label: 'Faster',
    },
  ];

  @observable instrument: Instrument[] = [
    {
      label: 'Show',
      value: undefined,
    },
    {
      label: 'Hide',
      value: false,
    },
    {
      label: 'Only',
      value: true,
    },
  ];
  @observable availableInCountry: Instrument[] = [
    {
      label: 'Show All',
      value: undefined,
    },
    {
      label: 'Hide Unavailable',
      value: true,
    },
  ];
  @observable cursor: number = null;
  @observable platformsAndLabel: { [key: string]: string } = {
    youtube: 'YouTube',
    instagram: 'Instagram',
    TikTok: 'TikTok',
    twitter: 'X',
    twitch: 'Twitch',
    snapchat: 'Snapchat',
    facebook: 'Facebook',
    vimeo: 'Vimeo',
    podcasting: 'Podcast',
    linkedin: 'LinkedIn',
  };
  @observable catalogueData: Catalogue[] = [
    {
      label: 'All',
      value: 'ALL',
    },
    {
      label: 'Premium',
      value: 'CHART',
    },
    {
      label: 'Included',
      value: 'PRODUCTION',
    },
  ];

  @observable sortModeManuallyChanged = false;

  @computed
  get searchVariables(): FindTracksQueryVariables {
    return this.searchState;
  }

  @computed
  get isValidatedSearch(): boolean {
    return !!this.selectedSuggestion && this.formattedValidatedSearchResults.length > 0;
  }

  @computed
  get tracksView(): TracksViewType {
    if (this.isValidatedSearch) {
      if (this.selectedSuggestion.type === 'playlist') return TracksViewType.HIDDEN;

      if (this.selectedSuggestion.type === 'artist') {
        return !isEmpty(this.validatedSearchTrackFilters) ? TracksViewType.ARTIST : TracksViewType.HIDDEN;
      }

      if (this.selectedSuggestion.type === 'track') {
        return TracksViewType.TRACK;
      }
    }

    return TracksViewType.DEFAULT;
  }

  @computed
  get genreCount(): number {
    const genreFilter = this.searchState.filters.genre;
    return genreFilter ? genreFilter.split(',').length : 0;
  }

  @computed
  get moodCount(): number {
    const moodFilter = this.searchState.filters.mood;
    return moodFilter ? moodFilter.split(',').length : 0;
  }

  @computed
  get filterCount(): number {
    const { genre, mood, durationMin, durationMax, tempoMin, tempoMax } = this.searchState.filters;

    const filter = Object.keys(this.searchState.filters).filter((value) => {
      if (value === 'matchArtistFallback' || value === 'availableIn') return false;
      return this.searchState.filters[value] !== this.defaultSearchState.filters[value];
    });
    const minAndMaxDurationChange =
      durationMin !== this.defaultSearchState.filters.durationMin &&
      durationMax !== this.defaultSearchState.filters.durationMax;

    const minAndMaxTempoChange =
      tempoMin !== this.defaultSearchState.filters.tempoMin && tempoMax !== this.defaultSearchState.filters.tempoMax;
    let filterCount = filter.length;
    if (minAndMaxDurationChange) {
      filterCount = filterCount - 1;
    }
    if (minAndMaxTempoChange) {
      filterCount = filterCount - 1;
    }
    if (genre) {
      const genres = genre.split(',');
      filterCount = filterCount + (genres.length - 1);
    }
    if (mood) {
      const moods = mood.split(',');
      filterCount = filterCount + (moods.length - 1);
    }
    return filterCount;
  }

  @computed
  get getFormattedSuggestions(): SearchSuggestionType[] {
    if (!this.suggestions) return [];

    const results = Object.keys(this.suggestions).reduce((arr, key) => {
      const newArr = this.suggestions[key].matches.results.map((result) => {
        if (key === 'playlist') {
          return formatGqlPlaylist(result);
        }
        return { ...result, type: entityMap[key] };
      });

      return [...arr, ...newArr];
    }, []);

    return results;
  }

  @computed
  get formattedValidatedSearchResults() {
    if (!this.validatedSearchResults) return [];

    // Validated playlist - return the playlist on its own
    if ('playlist' in this.validatedSearchResults) {
      return [formatGqlPlaylist(this.validatedSearchResults.playlist)];
    }

    if ('artist' in this.validatedSearchResults) {
      const isNonActive = this.validatedSearchResults.artist.is_non_active_artist;
      const similarArtists =
        this.validatedSearchResults.artist.similar?.map((similarArtist) => similarArtist.artist) || [];
      const playlists = this.validatedSearchResults.playlists.matches.results.map((playlist) => {
        return formatGqlPlaylist(playlist);
      });

      if (isNonActive && similarArtists.length) {
        return [this.validatedSearchResults.artist, ...similarArtists].map((artist) => {
          return {
            ...artist,
            type: 'artist',
          };
        });
      }

      return [{ ...this.validatedSearchResults.artist, type: 'artist' }, ...playlists];
    }

    if ('track' in this.validatedSearchResults) {
      const playlists = this.validatedSearchResults.playlists.matches.results.map((playlist) => {
        return formatGqlPlaylist(playlist);
      });

      return [{ ...this.validatedSearchResults.track, type: 'track' }, ...playlists];
    }

    return [];
  }

  @computed
  get allSearchSuggestions() {
    return this.showInstrumentalSuggestion
      ? [instrumentalEntity, ...this.getFormattedSuggestions, ...this.searchHistory]
      : [...this.getFormattedSuggestions, ...this.searchHistory];
  }

  @computed get defaultSearchState(): FindTracksQueryVariables {
    return getDefaultState();
  }
}
